import {
  buildRule,
  frequencyForRule,
  type MonthlyMode,
  type RuleHash,
  type RuleInput,
} from './rule';
import { applyValues, applyWeeks, readValues, readWeeks, togglePressed } from './selection';

export type PickerElements = {
  frequency: HTMLSelectElement;
  interval: HTMLInputElement;
  unit: HTMLElement;
  sections: HTMLElement[];
  days: HTMLButtonElement[];
  daysOfMonth: HTMLButtonElement[];
  weeks: HTMLButtonElement[];
  monthlyModes: HTMLInputElement[];
  monthDayGrid: HTMLElement;
  monthWeekGrid: HTMLElement;
};

export type PickerView = {
  write(rule: RuleHash | null): void;
  read(): RuleInput;
  rule(): RuleHash;
  toggle(button: HTMLButtonElement): void;
  showSections(): void;
  showMonthlyMode(): void;
  applyUnit(): void;
};

export function createPickerView(elements: PickerElements, weekStart: number): PickerView {
  const monthlyMode = (): MonthlyMode => {
    const checked = elements.monthlyModes.find((input) => input.checked);
    return checked?.value === 'day_of_week' ? 'day_of_week' : 'day_of_month';
  };

  const showMonthlyMode = (): void => {
    const mode = monthlyMode();
    elements.monthDayGrid.hidden = mode !== 'day_of_month';
    elements.monthWeekGrid.hidden = mode !== 'day_of_week';
  };

  const setMonthlyMode = (mode: MonthlyMode): void => {
    elements.monthlyModes.forEach((input) => {
      input.checked = input.value === mode;
    });
    showMonthlyMode();
  };

  const showSections = (): void => {
    const frequency = elements.frequency.value;
    elements.sections.forEach((section) => {
      section.hidden = section.dataset.frequency !== frequency;
    });
  };

  const applyUnit = (): void => {
    elements.unit.textContent = elements.frequency.selectedOptions[0]?.dataset.unit ?? '';
  };

  const read = (): RuleInput => ({
    frequency: elements.frequency.value,
    interval: Number.parseInt(elements.interval.value, 10) || 1,
    weekStart,
    days: readValues(elements.days),
    daysOfMonth: readValues(elements.daysOfMonth),
    weeks: readWeeks(elements.weeks),
    monthlyMode: monthlyMode(),
  });

  const write = (rule: RuleHash | null): void => {
    const validations = rule?.validations ?? {};
    const daysOfWeek = validations.day_of_week ?? {};

    elements.frequency.value = frequencyForRule(rule);
    elements.interval.value = String(rule?.interval ?? 1);

    applyValues(elements.days, validations.day ?? []);
    applyValues(elements.daysOfMonth, validations.day_of_month ?? []);
    applyWeeks(elements.weeks, daysOfWeek);

    setMonthlyMode(Object.keys(daysOfWeek).length > 0 ? 'day_of_week' : 'day_of_month');
    showSections();
    applyUnit();
  };

  return {
    write,
    read,
    rule: () => buildRule(read()),
    toggle: (button) => togglePressed(button),
    showSections,
    showMonthlyMode,
    applyUnit,
  };
}
